import { language } from '@lumen/platform';
import type { Workspace } from './workspace';

export interface User {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  /** Server accounts: whether the address was confirmed through the mailed link. */
  emailVerified?: boolean;
  /** Server accounts: whether notifications are also mailed. */
  emailNotifications?: boolean;
}

export interface WorkspaceInfo {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  role: 'owner' | 'member' | 'guest';
}

export interface Session {
  token: string;
  user: User;
  workspaces: WorkspaceInfo[];
}

export interface UploadResult {
  url: string;
  name: string;
  size: number;
  type: string;
}

export interface Unfurl {
  url: string;
  title: string;
  description: string;
  icon: string | null;
  image: string | null;
}

export class BackendError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

/** Everything the client needs from the server besides Yjs sync. Implemented over HTTP by `HttpBackend`. */
export interface BackendClient {
  signUp(input: { email: string; password: string; name: string; inviteToken?: string }): Promise<Session>;
  signIn(input: { email: string; password: string }): Promise<Session>;
  me(token: string): Promise<Session>;
  signOut(token: string): Promise<void>;
  createWorkspace(token: string, name: string): Promise<WorkspaceInfo>;
  upload(token: string, file: { name: string; type: string; blob: Blob }): Promise<UploadResult>;
  unfurl(token: string, url: string): Promise<Unfurl>;
  /** Fetches an uploaded or linked file's bytes (for downloads). */
  fetchFile(token: string, url: string): Promise<Blob>;
  request<T>(token: string | null, method: string, path: string, body?: unknown): Promise<T>;
  /** True when this browser's IndexedDB is the only copy of the data, so signing out must not clear it. */
  readonly localOnly?: boolean;
  /** Called once a workspace doc has loaded locally. Backends without a server use it to seed and update the doc. */
  workspaceOpened?(workspaceId: string, ws: Workspace): void;
}

/** The session "token" of a browser whose real token lives in the server's httpOnly cookie. */
export const COOKIE_SESSION = 'cookie';

export class HttpBackend implements BackendClient {
  /**
   * `cookies`: same-origin web app. The server keeps the session in an httpOnly cookie (scripts never see the
   * token) and every request carries the CSRF header. Without it (API clients, a web app on another host) the
   * session is a bearer token.
   */
  constructor(
    private readonly baseUrl = '',
    private readonly opts: { cookies?: boolean } = {},
  ) {}

  private auth(token: string | null): Record<string, string> {
    return {
      ...(token && token !== COOKIE_SESSION ? { authorization: `Bearer ${token}` } : {}),
      ...(this.opts.cookies ? { 'x-lumen-csrf': '1', 'x-lumen-session': COOKIE_SESSION } : {}),
      // The server answers (errors, mails, new workspaces) in the app's language (P-978). A safelisted header: no preflight.
      'accept-language': language(),
    };
  }

  async request<T>(token: string | null, method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...this.auth(token),
      },
      body: body !== undefined ? JSON.stringify(body) : null,
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) throw new BackendError(res.status, (data && (data.error as string)) || res.statusText);
    return data as T;
  }

  signUp(input: { email: string; password: string; name: string; inviteToken?: string }) {
    return this.request<Session>(null, 'POST', '/api/auth/signup', input);
  }

  signIn(input: { email: string; password: string }) {
    return this.request<Session>(null, 'POST', '/api/auth/login', input);
  }

  me(token: string) {
    return this.request<Session>(token, 'GET', '/api/me');
  }

  async signOut(token: string) {
    await this.request(token, 'POST', '/api/auth/logout');
  }

  createWorkspace(token: string, name: string) {
    return this.request<WorkspaceInfo>(token, 'POST', '/api/workspaces', { name });
  }

  async upload(token: string, file: { name: string; type: string; blob: Blob }) {
    const res = await fetch(`${this.baseUrl}/api/upload?name=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      headers: { ...this.auth(token), 'content-type': file.type || 'application/octet-stream' },
      body: file.blob,
    });
    if (!res.ok) throw new BackendError(res.status, 'upload failed');
    return (await res.json()) as UploadResult;
  }

  unfurl(token: string, url: string) {
    return this.request<Unfurl>(token, 'GET', `/api/unfurl?url=${encodeURIComponent(url)}`);
  }

  async fetchFile(token: string, url: string) {
    const sameOrigin = url.startsWith('/');
    const res = await fetch(sameOrigin ? `${this.baseUrl}${url}` : url, sameOrigin ? { headers: this.auth(token) } : {});
    if (!res.ok) throw new BackendError(res.status, 'download failed');
    return res.blob();
  }
}
